define([
    'lodash',
    'prop-types',
    'wixappsCore/core/proxySantaTypesDefinitions',
    'componentsCore',
    'wixappsCore/proxies/mixins/textProxy',
    'wixappsCore/core/typesConverter'
], function (_, PropTypes, proxySantaTypesDefinitions, componentsCore, textProxy, /** wixappsCore.typesConverter */typesConverter) {
    'use strict';

    const COMPONENT_TYPE = 'wysiwyg.viewer.components.WRichText';

    /**
     * Based on http://blog.stevenlevithan.com/archives/date-time-format
     * See full documentation at http://kb.wixpress.com/display/wixapps/Date
     */

    /*
     * Date Format 1.2.3
     * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
     * MIT license
     *
     * Includes enhancements by Scott Trenda <scott.trenda.net>
     * and Kris Kowal <cixar.com/~kris.kowal/>
     *
     * Accepts a date, a mask, or a date and a mask.
     * Returns a formatted version of the given date.
     * The date defaults to the current date/time.
     * The mask defaults to dateFormat.masks.default.
     */

    const dateFormat = (function () {
        const token = /d{1,5}|m{1,5}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g,
            timezone = /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[\-+]\d{4})?)\b/g,
            timezoneClip = /[^-+\dA-Z]/g,
            pad = function (val, len) {
                val = String(val);
                len = len || 2;
                while (val.length < len) { val = `0${val}`; }
                return val;
            };

        // Regexes and supporting functions are cached through closure
        return function (date, mask, utc) { // eslint-disable-line complexity
            const dF = dateFormat;

            // You can't provide utc if you skip other args (use the "UTC:" mask prefix)
            if (arguments.length === 1 && Object.prototype.toString.call(date) === '[object String]' && !/\d/.test(date)) {
                mask = date;
                date = undefined;
            }

            // Passing date through Date applies Date.parse, if necessary
            date = date ? new Date(date) : new Date();
            if (isNaN(date)) { throw new Error('invalid date'); }

            mask = String(dF.masks[mask] || mask || dF.masks.default);

            // Allow setting the utc argument via the mask
            if (mask.slice(0, 4) === 'UTC:') {
                mask = mask.slice(4);
                utc = true;
            }

            const getFunc = utc ? 'getUTC' : 'get',
                d = date[`${getFunc}Date`](),
                D = date[`${getFunc}Day`](),
                m = date[`${getFunc}Month`](),
                y = date[`${getFunc}FullYear`](),
                H = date[`${getFunc}Hours`](),
                M = date[`${getFunc}Minutes`](),
                s = date[`${getFunc}Seconds`](),
                L = date[`${getFunc}Milliseconds`](),
                o = utc ? 0 : date.getTimezoneOffset(),
                flags = {
                    d,
                    dd: pad(d),
                    ddd: dF.i18n.dayNames[D],
                    dddd: dF.i18n.dayNames[D + 7],
                    ddddd: dF.i18n.dayNames[D].toUpperCase(),
                    m: m + 1,
                    mm: pad(m + 1),
                    mmm: dF.i18n.monthNames[m],
                    mmmm: dF.i18n.monthNames[m + 12],
                    mmmmm: dF.i18n.monthNames[m].toUpperCase(),
                    yy: String(y).slice(2),
                    yyyy: y,
                    h: H % 12 || 12,
                    hh: pad(H % 12 || 12),
                    H,
                    HH: pad(H),
                    M,
                    MM: pad(M),
                    s,
                    ss: pad(s),
                    l: pad(L, 3),
                    L: pad(L > 99 ? Math.round(L / 10) : L),
                    t: H < 12 ? 'a' : 'p',
                    tt: H < 12 ? 'am' : 'pm',
                    T: H < 12 ? 'A' : 'P',
                    TT: H < 12 ? 'AM' : 'PM',
                    Z: utc ? 'UTC' : (String(date).match(timezone) || ['']).pop().replace(timezoneClip, ''),
                    o: (o > 0 ? '-' : '+') + pad(Math.floor(Math.abs(o) / 60) * 100 + Math.abs(o) % 60, 4), // eslint-disable-line no-mixed-operators
                    S: ['th', 'st', 'nd', 'rd'][d % 10 > 3 ? 0 : (d % 100 - d % 10 !== 10) * d % 10] // eslint-disable-line no-mixed-operators
                };

            return mask.replace(token, function ($0) {
                return $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
            });
        };
    }());

    // Some common format strings
    dateFormat.masks = {
        'default': 'mmmm dd, yyyy',
        shortDate: 'm/d/yy',
        mediumDate: 'mmm d, yyyy',
        longDate: 'mmmm d, yyyy',
        fullDate: 'dddd, mmmm d, yyyy',
        shortTime: 'h:MM TT',
        mediumTime: 'h:MM:ss TT',
        longTime: 'h:MM:ss TT Z',
        isoDate: 'yyyy-mm-dd',
        isoTime: 'HH:MM:ss',
        isoDateTime: "yyyy-mm-dd'T'HH:MM:ss",
        isoUtcDateTime: "UTC:yyyy-mm-dd'T'HH:MM:ss'Z'",
        eUshortDate: 'd/m/yy',
        eUshortDate2: 'd.m.yy'
    };

    // Internationalization strings
    dateFormat.i18n = {
        dayNames: [
            'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat',
            'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
        ],
        monthNames: [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
            'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
        ]
    };

    Object.freeze(dateFormat);

    function transformSkinProperties(refData) {
        const skinProperties = {
            richTextContainer: {
                style: {height: 'initial'}
            }
        };

        return _.defaultsDeep(skinProperties, refData);
    }

    /**
     * @class proxies.Date
     * @extends proxies.mixins.textProxy
     */
    return {
        mixins: [textProxy],
        statics: {
            componentType: COMPONENT_TYPE,
            format: {type: 'compProp', defaultValue: 'mmmm dd, yyyy'}
        },

        propTypes: {
            viewProps: PropTypes.shape({
                linkRenderInfo: proxySantaTypesDefinitions.linkRenderInfo.isRequired
            })
        },

        getInitialState() {
            return {
                zuluTime: true
            };
        },

        getFormattedDateString(data, props) {
            const format = this.getCompProp('format', props && props.viewDef) || this.constructor.format.defaultValue;
            const dateString = data.iso || data;
            let date = new Date(dateString);

            if (this.state.zuluTime) {
                let offset = date.getTimezoneOffset(); // returns offset from GMT in minutes
                offset *= 60000; // to convert the minutes to milliseconds
                date = new Date(date.valueOf() + offset);
            }

            // don't display formatted date for 'min-value' dates
            if (date.getTime() >= 0) {
                return dateFormat(date, format);
            }

            return '';
        },

        componentDidMount() {
            // In SSR world, we don't have the client timezone (to render the same date in server & client)
            /*eslint react/no-did-mount-set-state:0*/
            this.setState({
                zuluTime: false
            });
        },

        renderProxy() {
            const data = this.proxyData;
            const formattedText = this.createFormattedText(this.getFormattedDateString(data, this.props));

            const compData = typesConverter.richText(formattedText, data.links, this.props.viewProps.linkRenderInfo.pagesDataItemsMap);
            const props = this.getRichTextChildCompProps(COMPONENT_TYPE, transformSkinProperties, compData);

            return componentsCore.compRegistrar.getCompClass(COMPONENT_TYPE, true)(props);
        }
    };
});
